import React, { useState } from 'react';
import { BaseModal } from '../ui/modal';

interface LegalSection {
  id: string;
  title: string;
  content: React.ReactNode;
}

interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'privacy' | 'cookies' | 'legal' | 'terms';
}

const LegalModal: React.FC<LegalModalProps> = ({ isOpen, onClose, type }) => {
  const [activeSection, setActiveSection] = useState<string>('');

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(sectionId);
    }
  };

  const getModalData = () => {
    switch (type) {
      case 'privacy':
        return {
          title: 'Política de Privacidad',
          sections: getPrivacySections()
        };
      case 'cookies':
        return {
          title: 'Política de Cookies',
          sections: getCookiesSections()
        };
      case 'legal':
        return {
          title: 'Aviso Legal',
          sections: getLegalSections()
        };
      default:
        return { title: '', sections: [] };
    }
  };

  const getPrivacySections = (): LegalSection[] => [
    {
      id: 'identificacion',
      title: 'Información del Responsable',
      content: (
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            QUIÉN ES EL RESPONSABLE DE SUS DATOS PERSONALES
          </h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
            ELEVATE STUDIO S.L, es el responsable del tratamiento de los datos personales del usuario y le informa de que estos datos serán tratados de conformidad con lo dispuesto en el Reglamento (UE) 2016/679, de 27 de abril (GDPR), y la Ley Orgánica 3/2018, de 5 de diciembre (LOPDGDD).
          </p>
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <strong className="text-gray-900 dark:text-white">Denominación Social:</strong>
                <p className="text-gray-700 dark:text-gray-300">ELEVATE STUDIO S.L</p>
              </div>
              <div>
                <strong className="text-gray-900 dark:text-white">CIF:</strong>
                <p className="text-gray-700 dark:text-gray-300">B-75532226</p>
              </div>
              <div>
                <strong className="text-gray-900 dark:text-white">Domicilio:</strong>
                <p className="text-gray-700 dark:text-gray-300">C/ MARACENA Nº3, 18101-BELICENA (GRANADA)</p>
              </div>
              <div>
                <strong className="text-gray-900 dark:text-white">Teléfono:</strong>
                <p className="text-gray-700 dark:text-gray-300">684053267</p>
              </div>
              <div>
                <strong className="text-gray-900 dark:text-white">E-mail:</strong>
                <p className="text-gray-700 dark:text-gray-300">info@elevate.com.es</p>
              </div>
            </div>
          </div>
        </section>
      )
    },
    {
      id: 'principios',
      title: 'Principios Aplicables',
      content: (
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            PRINCIPIOS APLICABLES AL TRATAMIENTO DE LOS DATOS PERSONALES
          </h2>
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Principio de licitud, lealtad y transparencia</h3>
              <p className="text-blue-800 dark:text-blue-200 text-sm">
                Se requerirá en todo momento el consentimiento del usuario previa información completamente transparente de los fines para los cuales se recogen los datos personales.
              </p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <h3 className="font-semibold text-green-900 dark:text-green-100 mb-2">Principio de limitación de la finalidad</h3>
              <p className="text-green-800 dark:text-green-200 text-sm">
                Los datos personales serán recogidos con fines determinados, explícitos y legítimos.
              </p>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <h3 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-2">Principio de minimización de datos</h3>
              <p className="text-yellow-800 dark:text-yellow-200 text-sm">
                Los datos personales recogidos serán únicamente los estrictamente necesarios en relación con los fines para los que son tratados.
              </p>
            </div>
            <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-4">
              <h3 className="font-semibold text-teal-900 dark:text-teal-100 mb-2">Principio de exactitud</h3>
              <p className="text-teal-800 dark:text-teal-200 text-sm">
                Los datos personales deben ser exactos y estar siempre actualizados.
              </p>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Principio de limitación del plazo de conservación</h3>
              <p className="text-blue-800 dark:text-blue-200 text-sm">
                Los datos personales sólo serán mantenidos de forma que se permita la identificación del usuario durante el tiempo necesario para los fines de su tratamiento.
              </p>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <h3 className="font-semibold text-red-900 dark:text-red-100 mb-2">Principio de integridad y confidencialidad</h3>
              <p className="text-red-800 dark:text-red-200 text-sm">
                Los datos personales serán tratados de manera que se garantice su seguridad y confidencialidad.
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Principio de responsabilidad proactiva</h3>
              <p className="text-gray-800 dark:text-gray-200 text-sm">
                El responsable del tratamiento será responsable de asegurar que los principios anteriores se cumplen.
              </p>
            </div>
          </div>
        </section>
      )
    },
    {
      id: 'finalidades',
      title: 'Para qué usamos sus datos',
      content: (
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            PARA QUE TRATAMOS SUS DATOS PERSONALES Y PORQUÉ LO HACEMOS
          </h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            Según como hayamos obtenido sus datos personales en el sitio web, los trataremos de manera confidencial para alcanzar los siguientes fines:
          </p>
          
          <div className="space-y-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
              <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-3">Registro y uso de la plataforma GuideMe</h3>
              <ul className="list-disc ml-6 space-y-2 text-blue-800 dark:text-blue-200 text-sm">
                <li>Crear y gestionar su cuenta de usuario en la plataforma GuideMe</li>
                <li>Proporcionar acceso a los servicios de gestión de visitas guiadas y clientes</li>
                <li>Procesar pagos de suscripciones y servicios</li>
                <li>Enviar notificaciones relacionadas con el uso de la plataforma</li>
                <li>Proporcionar soporte técnico y atención al cliente</li>
              </ul>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
              <h3 className="font-semibold text-green-900 dark:text-green-100 mb-3">Comunicaciones comerciales</h3>
              <ul className="list-disc ml-6 space-y-2 text-green-800 dark:text-green-200 text-sm">
                <li>Envío de información sobre nuevas funcionalidades y actualizaciones</li>
                <li>Comunicaciones promocionales sobre planes y servicios</li>
                <li>Newsletter con consejos y mejores prácticas para profesionales del turismo</li>
                <li>Invitaciones a eventos y webinars relacionados con el sector</li>
              </ul>
              <p className="mt-3 text-green-800 dark:text-green-200 text-sm">
                <strong>Importante:</strong> Puede darse de baja de estas comunicaciones en cualquier momento desde su panel de usuario o contactando con nosotros.
              </p>
            </div>

            <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-6">
              <h3 className="font-semibold text-teal-900 dark:text-teal-100 mb-3">Mejora del servicio</h3>
              <ul className="list-disc ml-6 space-y-2 text-teal-800 dark:text-teal-200 text-sm">
                <li>Análisis de uso para mejorar la funcionalidad de la plataforma</li>
                <li>Investigación y desarrollo de nuevas características</li>
                <li>Personalización de la experiencia del usuario</li>
                <li>Detección y prevención de fraudes y usos indebidos</li>
              </ul>
            </div>
          </div>
        </section>
      )
    },
    {
      id: 'conservacion',
      title: 'Conservación de Datos',
      content: (
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            CONSERVACIÓN DE SUS DATOS PERSONALES
          </h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
            Sus datos personales se conservarán durante los siguientes períodos según el tipo de datos y finalidad:
          </p>
          
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Datos de la cuenta de usuario</h3>
              <p className="text-gray-700 dark:text-gray-300 text-sm">
                Se conservarán mientras mantenga activa su cuenta en GuideMe y hasta 3 años después de la cancelación de la suscripción para cumplir con obligaciones fiscales y contables.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Datos de facturación y pagos</h3>
              <p className="text-gray-700 dark:text-gray-300 text-sm">
                Se conservarán durante 6 años desde la emisión de la última factura, conforme a la legislación fiscal española.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Datos de marketing</h3>
              <p className="text-gray-700 dark:text-gray-300 text-sm">
                Se conservarán hasta que retire su consentimiento o solicite la baja de nuestras comunicaciones comerciales.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Logs y datos de uso</h3>
              <p className="text-gray-700 dark:text-gray-300 text-sm">
                Los datos de navegación y uso se conservarán de forma anonimizada durante un máximo de 2 años para fines estadísticos y de mejora del servicio.
              </p>
            </div>
          </div>

          <div className="mt-6 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
            <h3 className="font-semibold text-orange-900 dark:text-orange-100 mb-2">
              Eliminación segura de datos
            </h3>
            <p className="text-orange-800 dark:text-orange-200 text-sm">
              Una vez transcurridos los plazos de conservación, procederemos a la eliminación segura de sus datos personales con medidas técnicas que garanticen su anonimización o destrucción total.
            </p>
          </div>
        </section>
      )
    },
    {
      id: 'derechos',
      title: 'Sus Derechos',
      content: (
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            CUÁLES SON SUS DERECHOS Y CÓMO EJERCITARLOS
          </h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
            Como interesado que nos ha proporcionado sus datos personales, tiene pleno derecho para ejercitar los siguientes derechos conforme al GDPR y la LOPDGDD:
          </p>
          
          <div className="space-y-6">
            <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Derecho de Acceso
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                Puede solicitar información sobre qué datos personales tenemos sobre usted, para qué los usamos, con quién los compartimos y durante cuánto tiempo los conservamos.
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded text-sm text-blue-800 dark:text-blue-200">
                <strong>Cómo ejercerlo:</strong> Solicite un informe de sus datos desde su panel de usuario o contactando con info@elevate.com.es
              </div>
            </div>

            <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Derecho de Rectificación
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                Puede solicitar la corrección de datos inexactos o incompletos en cualquier momento.
              </p>
              <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded text-sm text-green-800 dark:text-green-200">
                <strong>Cómo ejercerlo:</strong> Actualice sus datos desde su perfil de usuario o solicite la corrección contactándonos.
              </div>
            </div>

            <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <svg className="w-5 h-5 mr-2 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Derecho de Supresión ("Derecho al Olvido")
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                Puede solicitar la eliminación de sus datos cuando ya no sean necesarios, retire su consentimiento, o se hayan tratado ilícitamente.
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded text-sm text-red-800 dark:text-red-200">
                <strong>Cómo ejercerlo:</strong> Solicite la eliminación desde la configuración de su cuenta o contactándonos. Nota: algunos datos pueden conservarse por obligaciones legales.
              </div>
            </div>

            <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <svg className="w-5 h-5 mr-2 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                Derecho a la Limitación del Tratamiento
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                Puede solicitar que suspendamos el tratamiento de sus datos en determinadas circunstancias.
              </p>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Cuándo aplicar:</strong> Si cuestiona la exactitud de los datos, el tratamiento es ilícito, o necesita que conservemos los datos para reclamaciones legales.
              </div>
            </div>

            <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <svg className="w-5 h-5 mr-2 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
                Derecho a la Portabilidad
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                Puede solicitar recibir sus datos en un formato estructurado y de uso común para transferirlos a otro responsable.
              </p>
              <div className="bg-teal-50 dark:bg-teal-900/20 p-3 rounded text-sm text-teal-800 dark:text-teal-200">
                <strong>Incluye:</strong> Datos de su cuenta, servicios creados, clientes, facturas y configuraciones. Disponible en formato JSON o CSV.
              </div>
            </div>

            <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <svg className="w-5 h-5 mr-2 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                Derecho de Oposición
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                Puede oponerse al tratamiento de sus datos por motivos relacionados con su situación particular o para marketing directo.
              </p>
              <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded text-sm text-orange-800 dark:text-orange-200">
                <strong>Marketing:</strong> Puede darse de baja de comunicaciones comerciales en cualquier momento desde su perfil de usuario.
              </div>
            </div>
          </div>

          <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
            <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Información Importante sobre el Ejercicio de Derechos
            </h3>
            <div className="space-y-3 text-blue-800 dark:text-blue-200 text-sm">
              <p><strong>Plazo de respuesta:</strong> Responderemos a su solicitud en un plazo máximo de 1 mes desde la recepción.</p>
              <p><strong>Verificación de identidad:</strong> Podremos solicitarle información adicional para verificar su identidad antes de procesar su solicitud.</p>
              <p><strong>Ejercicio gratuito:</strong> El ejercicio de estos derechos es gratuito, salvo que las solicitudes sean manifiestamente infundadas o excesivas.</p>
              <p><strong>Reclamación:</strong> Si considera que no hemos atendido adecuadamente su solicitud, puede presentar una reclamación ante la AEPD (www.aepd.es).</p>
            </div>
          </div>
        </section>
      )
    },
    {
      id: 'contacto',
      title: 'Datos de Contacto',
      content: (
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            DATOS DE CONTACTO PARA EJERCER SUS DERECHOS
          </h2>
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
            <div className="space-y-2 text-gray-700 dark:text-gray-300">
              <p><strong className="text-gray-900 dark:text-white">DENOMINACIÓN SOCIAL:</strong> ELEVATE STUDIO S.L</p>
              <p><strong className="text-gray-900 dark:text-white">CIF:</strong> B-75532226</p>
              <p><strong className="text-gray-900 dark:text-white">DOMICILIO:</strong> C/ MARACENA Nº3, 18101-BELICENA (GRANADA)</p>
              <p><strong className="text-gray-900 dark:text-white">TELÉFONO:</strong> 684053267</p>
              <p><strong className="text-gray-900 dark:text-white">E-MAIL:</strong> info@elevate.com.es</p>
            </div>
          </div>
        </section>
      )
    }
  ];

  const getCookiesSections = (): LegalSection[] => [
    {
      id: 'que-son',
      title: 'Qué son las Cookies',
      content: (
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            QUÉ SON LAS COOKIES
          </h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            Una cookie es un fichero que se descarga en su ordenador, teléfono móvil, tablet, etc, al acceder a determinados sitios web. Las cookies permiten a un sitio web, entre otras cosas, almacenar y recuperar información sobre los hábitos de navegación de un usuario.
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
            <li>Permiten que el sitio web funcione de forma más ágil y adaptado a las preferencias de los usuarios</li>
            <li>Establecen niveles de protección y seguridad</li>
            <li>Permiten conocer datos estadísticos para mejorar la experiencia</li>
            <li>Optimizan la publicidad que mostramos a los usuarios</li>
          </ul>
        </section>
      )
    },
    {
      id: 'tipos',
      title: 'Tipos de Cookies',
      content: (
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            TIPOS DE COOKIES QUE UTILIZAMOS
          </h2>
          <div className="space-y-4">
            <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                Cookies Técnicas (Necesarias)
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-600">
                      <th className="text-left py-2 font-medium text-gray-900 dark:text-white">Cookie</th>
                      <th className="text-left py-2 font-medium text-gray-900 dark:text-white">Propósito</th>
                      <th className="text-left py-2 font-medium text-gray-900 dark:text-white">Duración</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700 dark:text-gray-300">
                    <tr className="border-b border-gray-100 dark:border-gray-700">
                      <td className="py-2 font-mono">token</td>
                      <td className="py-2">Autenticación de usuario</td>
                      <td className="py-2">24 horas</td>
                    </tr>
                    <tr className="border-b border-gray-100 dark:border-gray-700">
                      <td className="py-2 font-mono">guideme-cookie-consent</td>
                      <td className="py-2">Preferencias de cookies</td>
                      <td className="py-2">1 año</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      )
    },
    {
      id: 'gestion',
      title: 'Gestión de Cookies',
      content: (
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            CÓMO GESTIONAR LAS COOKIES
          </h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            Puede configurar su navegador para aceptar o rechazar cookies automáticamente, o para que le avise cuando se va a almacenar una cookie en su dispositivo.
          </p>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-blue-800 dark:text-blue-200 text-sm">
              <strong>Configurar Cookies:</strong> Puede cambiar sus preferencias de cookies en cualquier momento desde el botón "Configurar Cookies" en el pie de página.
            </p>
          </div>
        </section>
      )
    }
  ];

  const getLegalSections = (): LegalSection[] => [
    {
      id: 'identificacion',
      title: 'Identificación',
      content: (
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            DATOS IDENTIFICATIVOS DEL RESPONSABLE
          </h2>
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <strong className="text-gray-900 dark:text-white">Denominación Social:</strong>
                <p className="text-gray-700 dark:text-gray-300">ELEVATE STUDIO S.L</p>
              </div>
              <div>
                <strong className="text-gray-900 dark:text-white">CIF:</strong>
                <p className="text-gray-700 dark:text-gray-300">B-75532226</p>
              </div>
              <div>
                <strong className="text-gray-900 dark:text-white">Domicilio:</strong>
                <p className="text-gray-700 dark:text-gray-300">C/ MARACENA Nº3, 18101-BELICENA (GRANADA)</p>
              </div>
              <div>
                <strong className="text-gray-900 dark:text-white">E-mail:</strong>
                <p className="text-gray-700 dark:text-gray-300">info@elevate.com.es</p>
              </div>
            </div>
          </div>
        </section>
      )
    },
    {
      id: 'propiedad',
      title: 'Propiedad Intelectual',
      content: (
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            DERECHOS DE PROPIEDAD INTELECTUAL E INDUSTRIAL
          </h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            El sitio web, incluyendo su programación, edición, compilación y demás elementos necesarios para su funcionamiento, los diseños, logotipos, textos, etc, son propiedad del responsable o dispone de licencia o autorización expresa por parte de los autores.
          </p>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            Todos los contenidos se encuentran debidamente protegidos por la normativa de propiedad intelectual e industrial, así como inscritos en los registros públicos correspondientes.
          </p>
        </section>
      )
    },
    {
      id: 'responsabilidad',
      title: 'Exención de Responsabilidades',
      content: (
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            EXENCIÓN DE RESPONSABILIDADES
          </h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            El responsable se exime de cualquier tipo de responsabilidad derivada de la información publicada en su sitio web, siempre que esta información haya sido manipulada o introducida por un tercero ajeno al mismo.
          </p>
        </section>
      )
    }
  ];

  const { title, sections } = getModalData();

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} size="4xl" showCloseButton>
      <div className="-m-6">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex-1 text-center">
            <h1 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
              {title}
            </h1>
          </div>
          <div className="w-8"></div>
        </div>

        {/* Content - Layout Responsive */}
        <div className="flex overflow-hidden" style={{ height: 'calc(80vh - 80px)' }}>
          {/* Sidebar Navigation - Solo desktop */}
          <div className="hidden lg:block w-64 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 p-6 overflow-y-auto">
            <nav className="space-y-2">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    activeSection === section.id
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {section.title}
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Navigation Pills for Mobile */}
            <div className="lg:hidden p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
              <div className="flex overflow-x-auto space-x-2 pb-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      activeSection === section.id
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                        : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                    }`}
                  >
                    {section.title}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 md:p-8 max-w-4xl">
              <div className="prose prose-gray dark:prose-invert max-w-none">
                {sections.map((section, index) => (
                  <div key={section.id} id={section.id} className={index > 0 ? 'mt-8 md:mt-12' : ''}>
                    {section.content}
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="mt-8 md:mt-12 pt-6 md:pt-8 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Última actualización: 8 de agosto de 2025
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default LegalModal;